'use strict';

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHomepageItems } from '@src/components/hooks/useHomepageItems';
import { useUserWishlist } from '@src/components/hooks/useUserWishlist';
import { LatestListingsSkeleton } from './LatestListingsSkeleton';
import { LatestListingsError } from './LatestListingsError';
import { LatestListingsContent } from './LatestListingsContent';

/**
 * Client-side LatestListings component.
 *
 * Only mounted after hydration (parent ensures this).
 * Fetches items and wishlist via React Query hooks,
 * merges wishlist state, and renders the appropriate UI.
 */
export const LatestListingsClient = ({ userID, layoutItemCount }) => {
    const { items, isLoading, isError, refetch } = useHomepageItems();
    const { wishlistItems } = useUserWishlist(userID);

    const itemsWithFavorites = useMemo(() => {
        if (!items.length) return [];
        if (!wishlistItems.length) return items;

        const favoritedIds = new Set(wishlistItems.map(w => w.itemId));
        return items.map(item => ({
            ...item,
            isFavorited: favoritedIds.has(item.id)
        }));
    }, [items, wishlistItems]);

    if (isLoading) return <LatestListingsSkeleton count={layoutItemCount} />;
    if (isError) return <LatestListingsError onRetry={refetch} />;
    if (!itemsWithFavorites.length) return null;

    return <LatestListingsContent items={itemsWithFavorites} userID={userID} />;
};

LatestListingsClient.propTypes = {
    userID: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null, undefined])]),
    layoutItemCount: PropTypes.number
};
